import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatUnit, formatAddressUnit } from "@/lib/formatters";
import { toast } from "sonner";

/**
 * Sweeper Radar Whiteboard v2 — single-screen dispatch board.
 * Approved mockup: /public/mockups/sweeper-radar-v2.html
 * Fallback: append ?v=classic to fall back to the 3-step wizard Lineup.jsx.
 *
 * 3-column layout:
 *   Left    → Techs (working tomorrow)
 *   Middle  → Truck cards with pinned WOs (auto-cluster button per truck)
 *   Right   → Unpinned WO pool (search + filters)
 *
 * Everything uses existing pin/unpin endpoints. Publish uses /lineup/commit.
 * NEW backend: POST /api/jobs/auto-cluster reorders a truck's pinned WOs.
 */

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDays(iso, n) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return toISO(dt);
}
// Compute YYYY-MM-DD in an IANA timezone (tenant TZ). Falls back to browser
// local if `tz` is invalid — never throws. This is what makes the Radar
// Today/Tomorrow chips mean "Marcus's Today" (Philly) even when the VA in
// Philippines is 12 hours ahead of the Philly crew.
function todayInTz(tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(new Date());
    const y = parts.find(p => p.type === "year")?.value;
    const m = parts.find(p => p.type === "month")?.value;
    const d = parts.find(p => p.type === "day")?.value;
    if (y && m && d) return `${y}-${m}-${d}`;
  } catch (_) {}
  return toISO(new Date());
}
function tomorrowISO() { const d = new Date(); d.setDate(d.getDate() + 1); return toISO(d); }
function todayISO() { return toISO(new Date()); }

export default function LineupV2() {
  const [tenantTz, setTenantTz] = useState("America/New_York");
  const [browserTz] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
  });
  const [date, setDate] = useState(() => {
    // Default to tomorrow in tenant TZ so dispatchers prep the crew's next day
    const t = todayInTz("America/New_York");
    return addDays(t, 1);
  });
  const [workers, setWorkers] = useState([]);
  const [pool, setPool] = useState([]);          // unpinned open jobs
  const [pins, setPins] = useState([]);           // pinned WOs (all trucks)
  const [rollCall, setRollCall] = useState({});   // {worker_id: true} — trucks visible in middle col
  const [busyTruck, setBusyTruck] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");    // all | emergency | turns
  const [pinTarget, setPinTarget] = useState(null); // { job } — modal to pick a truck
  const [mobileTab, setMobileTab] = useState("trucks"); // techs | trucks | pool
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 900);
  const [weather, setWeather] = useState([]);     // 7-day forecast
  const [leftover, setLeftover] = useState({ count: 0, jobs: [], unique_dates: [] });
  const [rolling, setRolling] = useState(false);

  useEffect(() => {
    const on = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  // Load tenant timezone from settings so Radar chips mean the crew's day,
  // not the dispatcher's laptop day (protects the Philippines VA workflow).
  useEffect(() => {
    api.get("/settings").then(r => {
      const tz = r?.data?.tenant_timezone || "America/New_York";
      setTenantTz(tz);
    }).catch(() => {});
  }, []);

  // Load 7-day weather once on mount (cached by browser + backend)
  useEffect(() => {
    api.get("/weather/forecast?days=7").then(r => setWeather(r.data.days || [])).catch(() => setWeather([]));
  }, []);

  const loadAll = async () => {
    const [wr, jr, pr, lo] = await Promise.all([
      api.get("/assignable-users").catch(() => ({ data: [] })),
      api.get("/jobs", { params: { limit: 1000 } }).catch(() => ({ data: [] })),
      api.get(`/jobs/sweeper-pins?date=${date}`).catch(() => ({ data: [] })),
      api.get(`/jobs/sweeper-pins/leftover?before=${date}`).catch(() => ({ data: { count: 0, jobs: [], unique_dates: [] } })),
    ]);
    const ws = (wr.data || []).filter(u => u.role === "worker" && u.active !== false);
    setWorkers(ws);
    setRollCall(prev => {
      // Preserve existing roll-call choices; default new workers to ON
      const next = { ...prev };
      ws.forEach(w => { if (!(w.id in next)) next[w.id] = true; });
      return next;
    });
    setPins(pr.data || []);
    setLeftover(lo.data || { count: 0, jobs: [], unique_dates: [] });
    const pinnedIds = new Set((pr.data || []).map(p => p.id));
    const jobs = (jr.data || []).filter(j =>
      !pinnedIds.has(j.id) &&
      !["completed", "canceled", "merged"].includes((j.status || "").toLowerCase())
    );
    setPool(jobs);
  };

  const rollForward = async () => {
    if (!window.confirm(`Roll ${leftover.count} leftover pin${leftover.count === 1 ? "" : "s"} forward to ${date}?`)) return;
    setRolling(true);
    try {
      const r = await api.post("/jobs/sweeper-pin/roll-forward", { from_date: date, to_date: date });
      toast.success(`Rolled ${r.data.rolled} pin${r.data.rolled === 1 ? "" : "s"} to ${date}`);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not roll forward"));
    } finally { setRolling(false); }
  };

  // "Clear past pins" — user asked for a way to nuke stale leftovers instead
  // of rolling them (e.g. jobs closed by ClickUp that still show up as
  // leftover because the pin never got unset). Bulk-unpin all leftover IDs.
  // Sweep Pool "Divide" — multi-select from the pool, auto-split across
  // working trucks by distance (haversine). Preview before commit.
  const [selectedPool, setSelectedPool] = useState(() => new Set());
  const [splitPreview, setSplitPreview] = useState(null); // { assignments, unlocatable_count }
  const [splitBusy, setSplitBusy] = useState(false);

  const togglePoolSelect = (jobId) => {
    setSelectedPool(prev => {
      const n = new Set(prev);
      if (n.has(jobId)) n.delete(jobId); else n.add(jobId);
      return n;
    });
  };

  const selectAllFiltered = () => {
    setSelectedPool(new Set(filteredPool.map(j => j.id)));
  };
  const clearPoolSelection = () => setSelectedPool(new Set());

  const runAutoSplit = async (dryRun) => {
    const jobIds = Array.from(selectedPool);
    const truckKeys = workingWorkers.map(w => w.id);
    if (!jobIds.length) { toast.error("Pick at least one WO from the pool"); return; }
    if (!truckKeys.length) { toast.error("Turn on at least one tech first"); return; }
    setSplitBusy(true);
    try {
      const r = await api.post("/jobs/sweeper-auto-split", {
        job_ids: jobIds, truck_keys: truckKeys, date, dry_run: !!dryRun,
      });
      if (dryRun) {
        setSplitPreview(r.data);
      } else {
        toast.success(`Divided ${r.data.pinned} WO${r.data.pinned === 1 ? "" : "s"} across ${truckKeys.length} trucks`);
        setSplitPreview(null);
        setSelectedPool(new Set());
        await loadAll();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Auto-split failed"));
    } finally { setSplitBusy(false); }
  };

  const [clearing, setClearing] = useState(false);
  const clearLeftover = async () => {
    if (!leftover.count) return;
    if (!window.confirm(
      `Clear ${leftover.count} pinned WO${leftover.count === 1 ? "" : "s"} from past dates? ` +
      `They stay open (not completed) but drop off Sweep Radar.`
    )) return;
    const jobIds = (leftover.jobs || []).map(j => j.id || j._id).filter(Boolean);
    if (!jobIds.length) { toast.error("Nothing to clear"); return; }
    setClearing(true);
    try {
      const r = await api.post("/jobs/sweeper-unpin", { job_ids: jobIds });
      toast.success(`Cleared ${r.data.unpinned} pin${r.data.unpinned === 1 ? "" : "s"}`);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not clear"));
    } finally { setClearing(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [date]);

  // ── Derived ──────────────────────────────────────────────────────────
  const workingWorkers = useMemo(() => workers.filter(w => rollCall[w.id]), [workers, rollCall]);
  const offWorkers = useMemo(() => workers.filter(w => !rollCall[w.id]), [workers, rollCall]);

  const trucks = useMemo(() => {
    // A truck exists for every "working" worker; empty trucks still show
    return workingWorkers.map(w => ({
      truck_key: w.id,
      label: w.name || w.email || "Truck",
      initials: (w.name || w.email || "??").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase(),
      jobs: pins.filter(p => p.truck_key === w.id).sort((a, b) => (a.route_order ?? 999) - (b.route_order ?? 999)),
    }));
  }, [workingWorkers, pins]);

  const orphanPins = useMemo(() => {
    const truckKeys = new Set(workingWorkers.map(w => w.id));
    return pins.filter(p => p.truck_key && !truckKeys.has(p.truck_key));
  }, [pins, workingWorkers]);

  const totalPinned = pins.length;
  const totalTrucks = trucks.filter(t => t.jobs.length > 0).length;

  const filteredPool = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pool.filter(j => {
      if (filter === "emergency" && (j.priority || "").toLowerCase() !== "urgent" && (j.priority || "").toLowerCase() !== "emergency") return false;
      if (filter === "turns" && !j.is_turn) return false;
      if (!q) return true;
      const hay = `${j.work_order_number || ""} ${j.property_address || ""} ${j.unit || ""} ${j.title || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pool, search, filter]);

  // Search also spans pins so the dispatcher isn't blind to WOs already on a
  // truck. Fixes the "I searched 3666 in Radar and it didn't show, but the
  // global search up top found it" bug.
  const pinnedHits = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return pins.filter(p => {
      const hay = `${p.work_order_number || ""} ${p.property_address || ""} ${p.unit || ""} ${p.title || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pins, search]);

  const truckLabelFor = (truckKey) => {
    const w = workers.find(x => x.id === truckKey);
    return w ? (w.name || w.email || "Truck") : "another truck";
  };

  // ── Actions ──────────────────────────────────────────────────────────
  const toggleRollCall = (workerId) => {
    setRollCall(prev => ({ ...prev, [workerId]: !prev[workerId] }));
  };

  const pinToTruck = async (job, truck) => {
    try {
      await api.post("/jobs/sweeper-pin", {
        job_ids: [job.id],
        truck_key: truck.truck_key,
        truck_label: truck.label,
        date,
      });
      toast.success(`Pinned to ${truck.label}`);
      setPinTarget(null);
      // On mobile, jump to the Trucks tab so user sees where it landed
      if (typeof window !== "undefined" && window.innerWidth < 900) setMobileTab("trucks");
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not pin");
    }
  };

  const unpin = async (jobId) => {
    try {
      await api.post("/jobs/sweeper-unpin", { job_ids: [jobId] });
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not unpin");
    }
  };

  // Manual drag/bump reorder — sends the new order to the backend and
  // reloads so route_order is sticky across refreshes & other devices.
  const reorderTruck = async (truck, orderedJobIds) => {
    try {
      await api.post("/jobs/sweeper-reorder", {
        truck_key: truck.truck_key,
        date,
        ordered_job_ids: orderedJobIds,
      });
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reorder failed");
    }
  };

  const markDone = async (job) => {
    if (!window.confirm(`Mark WO ${job.work_order_number || job.id?.slice(0,6)} as completed?\n\nUse this only for jobs the crew actually finished (or WOs that were logged by mistake).`)) return;
    try {
      await api.post(`/jobs/${job.id}/complete`, {
        status: "completed",
        notes: "Marked done from Sweeper Radar whiteboard by dispatcher",
      });
      toast.success(`Marked done · WO ${job.work_order_number || ""}`);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not mark done");
    }
  };

  const autoCluster = async (truck, targetCount) => {
    setBusyTruck(truck.truck_key);
    try {
      const r = await api.post("/jobs/auto-cluster", {
        truck_key: truck.truck_key,
        truck_label: truck.label,
        date,
        target_count: targetCount || 0,
      });
      const meters = r.data.total_meters || 0;
      const miles = (meters / 1609.34).toFixed(1);
      const added = r.data.added_count || 0;
      const warn = r.data.fill_warning;
      const repaired = r.data.seeds_repaired || [];
      const unrepairable = r.data.seeds_unrepairable || [];
      if (repaired.length > 0) {
        toast.success(`${truck.label}: geocoded ${repaired.length} seed WO${repaired.length > 1 ? "s" : ""} on the fly — ${repaired.map(s => s.wo || s.title).join(", ")}`, { duration: 4500 });
      }
      if (warn === "no_geocoded_seed") {
        // Show WHICH seed(s) need attention with a clickable link to open the WO.
        if (unrepairable.length > 0) {
          const first = unrepairable[0];
          const label = first.wo ? `WO ${first.wo}` : (first.title || "seed WO");
          const reason = first.reason === "no_address"
            ? "no address on file — open the WO and add one"
            : "address didn't geocode — fix the address or set the pin manually";
          toast.error(
            `${truck.label} · ${label}: ${reason}${first.address ? ` (${first.address})` : ""}`,
            {
              duration: 8000,
              action: first.job_id ? {
                label: "Open WO",
                onClick: () => { window.location.href = `/admin/jobs/${first.job_id}`; },
              } : undefined,
            },
          );
        } else {
          toast.error(`${truck.label}: seed WO has no address on map — pin one more (with an address) or fix the seed WO's address first`, { duration: 6000 });
        }
      } else if (warn === "pool_empty") {
        toast(`${truck.label}: nothing new nearby to add · ${r.data.ordered.length} stops reordered`);
      } else if (added > 0) {
        toast.success(`${truck.label}: +${added} filled · ${r.data.ordered.length} stops · ${miles} mi drive`);
      } else {
        toast.success(`${truck.label}: ${r.data.ordered.length} stops · ${miles} mi drive`);
      }
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Auto-cluster failed");
    } finally { setBusyTruck(null); }
  };

  const publish = async () => {
    if (totalPinned === 0) { toast.error("Nothing pinned yet"); return; }
    if (!window.confirm(`Publish ${totalPinned} jobs across ${totalTrucks} trucks for ${date}?`)) return;
    setPublishing(true);
    try {
      const assignments = trucks
        .filter(t => t.jobs.length > 0)
        .map(t => ({ lead_id: t.truck_key, job_ids: t.jobs.map(j => j.id) }));
      const r = await api.post("/lineup/commit", { date, assignments });
      toast.success(`Published — ${r.data.committed} jobs assigned for ${date}`);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Publish failed");
    } finally { setPublishing(false); }
  };

  // ── Render ──────────────────────────────────────────────────────────
  const dateChip = (label, iso) => (
    <button onClick={() => setDate(iso)} data-testid={`v2-date-${label.toLowerCase()}`}
      className={`h-9 px-3.5 rounded-lg text-[13px] font-semibold border transition-colors
        ${date === iso ? "bg-[#00C4CC] border-[#00C4CC] text-white"
          : "bg-white border-slate-200 text-[#0B1F3A] hover:border-[#00C4CC]"}`}>
      {label}
    </button>
  );

  return (
    <AdminShell>
      <div className="max-w-[1440px] mx-auto" data-testid="lineup-v2">
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-white px-3 sm:px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg sm:text-xl font-bold text-[#0B1F3A] tracking-tight">Sweeper Radar</h1>
            <span className="hidden sm:inline text-[11px] font-bold uppercase tracking-widest text-[#008a8f] bg-[#E6FAFB] px-2.5 py-1 rounded-full">
              Whiteboard
            </span>
            {browserTz !== tenantTz && (
              <span className="hidden sm:inline text-[10px] font-black uppercase tracking-widest text-fuchsia-800 bg-fuchsia-100 border border-fuchsia-300 px-2 py-1 rounded-full"
                title={`Your device: ${browserTz}. Radar dates below are anchored to the crew's timezone: ${tenantTz}.`}
                data-testid="v2-tz-badge">
                🌐 Crew TZ · {tenantTz.split("/").pop().replace(/_/g, " ")}
              </span>
            )}
            <div className="ml-auto flex items-center gap-1 flex-wrap">
              {dateChip("Today", todayInTz(tenantTz))}
              {dateChip("Tomorrow", addDays(todayInTz(tenantTz), 1))}
              <span className="hidden sm:contents">
                {dateChip("+2", addDays(todayInTz(tenantTz), 2))}
                {dateChip("+3", addDays(todayInTz(tenantTz), 3))}
              </span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                data-testid="v2-date-input"
                className="h-9 px-2 rounded-lg border border-slate-200 text-[12px] font-semibold text-[#0B1F3A]"/>
              <WeatherChip date={date} weather={weather}/>
              <Link to={`/admin/lineup?v=classic`} data-testid="v2-open-classic"
                className="text-[11px] font-bold text-slate-500 hover:text-[#0B1F3A] underline ml-1">
                Classic
              </Link>
            </div>
          </div>

          {/* Mobile tabs (hidden ≥900px) */}
          {isMobile && (
            <div className="flex mt-3 -mx-3 border-b border-slate-100" data-testid="v2-mobile-tabs">
              {[
                ["techs",  "Techs",  workingWorkers.length],
                ["trucks", "Trucks", totalPinned],
                ["pool",   "Pool",   filteredPool.length],
              ].map(([key, label, count]) => (
                <button key={key} onClick={() => setMobileTab(key)}
                  data-testid={`v2-tab-${key}`}
                  className={`flex-1 py-3 text-[13px] font-bold border-b-2 transition-colors
                    ${mobileTab === key ? "border-[#00C4CC] text-[#0B1F3A]" : "border-transparent text-slate-500"}`}>
                  {label}
                  <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full
                    ${mobileTab === key ? "bg-[#00C4CC] text-white" : "bg-slate-200 text-slate-600"}`}>
                    {count}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Leftover-pin banner: open WOs still tagged to a past date
              (jobs "stuck" on yesterday's radar). One-tap roll-forward. */}
          {leftover.count > 0 && (
            <div className="mt-3 rounded-xl border-2 border-amber-400 bg-amber-50 px-3 py-2 flex items-center gap-3 flex-wrap"
              data-testid="v2-leftover-banner">
              <div className="text-lg">📌</div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-black uppercase tracking-widest text-amber-900">
                  {leftover.count} WO{leftover.count === 1 ? "" : "s"} left pinned to past dates
                </div>
                <div className="text-[10px] text-amber-800 truncate">
                  {leftover.unique_dates.slice(0, 4).join(" · ")}{leftover.unique_dates.length > 4 ? " · …" : ""}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={rollForward} disabled={rolling || clearing}
                  data-testid="v2-roll-forward"
                  title="Move leftovers to today's Sweep Radar"
                  className="h-9 px-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-black uppercase text-[10px] tracking-widest rounded-md">
                  {rolling ? "Rolling…" : `Roll → ${date.slice(5)}`}
                </button>
                <button onClick={clearLeftover} disabled={rolling || clearing}
                  data-testid="v2-clear-leftover"
                  title="Unpin these WOs (they stay open but leave Sweep Radar)"
                  className="h-9 px-3 bg-white hover:bg-slate-100 disabled:opacity-50 border-2 border-amber-500 text-amber-800 font-black uppercase text-[10px] tracking-widest rounded-md">
                  {clearing ? "Clearing…" : "Clear Past"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Board: 3-col on desktop, tabbed single-col on mobile */}
        <div className={isMobile ? "p-3" : "grid gap-4 p-5"}
          style={isMobile ? undefined : { gridTemplateColumns: "300px 1fr 320px", minHeight: "calc(100vh - 130px)" }}>

          {/* LEFT — Techs */}
          <div data-testid="v2-col-techs" className={isMobile && mobileTab !== "techs" ? "hidden" : ""}>
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center justify-between">
              <span>Techs</span>
              <span className="bg-[#0B1F3A] text-white text-[10px] px-2 py-0.5 rounded-full">
                {workingWorkers.length} / {workers.length}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {workingWorkers.map(w => (
                <TechChip key={w.id} worker={w} on onClick={() => toggleRollCall(w.id)}
                  jobCount={pins.filter(p => p.truck_key === w.id).length}/>
              ))}
            </div>
            {offWorkers.length > 0 && (
              <>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-5 mb-2">
                  Off tomorrow ({offWorkers.length})
                </div>
                <div className="flex flex-col gap-2">
                  {offWorkers.map(w => (
                    <TechChip key={w.id} worker={w} off onClick={() => toggleRollCall(w.id)}/>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* MIDDLE — Trucks */}
          <div data-testid="v2-col-trucks" className={isMobile && mobileTab !== "trucks" ? "hidden" : ""}>
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center justify-between">
              <span>Trucks · {date}</span>
              <span className="bg-[#00C4CC] text-white text-[10px] px-2 py-0.5 rounded-full">
                {totalPinned} pinned
              </span>
            </div>
            <div className="flex flex-col gap-4">
              {trucks.length === 0 && (
                <div className="bg-white rounded-2xl p-8 text-center text-sm text-slate-500 border-2 border-dashed border-slate-200">
                  No trucks — {isMobile ? "flip to the Techs tab and turn one ON" : "turn a tech ON in the left column"}.
                </div>
              )}
              {trucks.map(t => (
                <TruckCard key={t.truck_key} truck={t}
                  busy={busyTruck === t.truck_key}
                  onAutoCluster={(target) => autoCluster(t, target)}
                  onUnpin={unpin}
                  onMarkDone={markDone}
                  onReorder={(orderedIds) => reorderTruck(t, orderedIds)}
                  date={date}
                  onPromoted={loadAll}/>
              ))}
              {orphanPins.length > 0 && (
                <div className="bg-amber-50 rounded-2xl p-4 border-2 border-amber-300" data-testid="v2-orphan-pins">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-amber-800 mb-1">
                    Orphan pins ({orphanPins.length})
                  </div>
                  <div className="text-xs text-amber-900 mb-2">These WOs are pinned to techs who aren't working tomorrow.</div>
                  {orphanPins.map(j => (
                    <div key={j.id} className="flex items-center justify-between py-1 text-xs">
                      <span><b>WO {j.work_order_number}</b> · {j.property_address} · assigned {j.truck_label}</span>
                      <button onClick={() => unpin(j.id)} className="text-red-600 hover:underline font-bold">Unpin</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Unpinned pool */}
          <div data-testid="v2-col-pool" className={isMobile && mobileTab !== "pool" ? "hidden" : ""}>
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center justify-between">
              <span>Unpinned WOs</span>
              <span className={`text-white text-[10px] px-2 py-0.5 rounded-full ${filteredPool.length > 0 ? "bg-red-600" : "bg-slate-400"}`}>
                {filteredPool.length}
              </span>
            </div>
            {/* Divide Pool bar — multi-select & auto-split by distance */}
            {filteredPool.length > 0 && (
              <div className="mb-2 rounded-lg border-2 border-[#00C4CC] bg-cyan-50 p-2" data-testid="v2-pool-divide-bar">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] font-black uppercase tracking-widest text-cyan-800">
                    Sweep Pool
                  </span>
                  {selectedPool.size > 0 ? (
                    <>
                      <span className="text-[10px] font-bold text-cyan-900 bg-white px-1.5 py-0.5 rounded-full">
                        {selectedPool.size} picked
                      </span>
                      <button onClick={clearPoolSelection} data-testid="v2-pool-clear-selection"
                        className="text-[10px] font-black text-slate-500 hover:text-slate-900 ml-auto">
                        clear
                      </button>
                    </>
                  ) : (
                    <button onClick={selectAllFiltered} data-testid="v2-pool-select-all"
                      className="text-[10px] font-black uppercase tracking-widest text-cyan-800 hover:text-cyan-950 ml-auto">
                      select all {filteredPool.length}
                    </button>
                  )}
                </div>
                <button onClick={() => runAutoSplit(true)}
                  disabled={splitBusy || !selectedPool.size || !workingWorkers.length}
                  data-testid="v2-pool-divide-btn"
                  title={!workingWorkers.length ? "Turn on at least one tech on the left first" : `Divide across ${workingWorkers.length} working truck${workingWorkers.length === 1 ? "" : "s"}`}
                  className="mt-1.5 w-full h-9 rounded-md bg-[#00C4CC] hover:bg-[#00a8af] disabled:opacity-40 disabled:cursor-not-allowed text-white font-black uppercase text-[10px] tracking-widest">
                  {splitBusy ? "Dividing…" : `⚡ Divide across ${workingWorkers.length} truck${workingWorkers.length === 1 ? "" : "s"}`}
                </button>
              </div>
            )}
            <div className="mb-2">
              <input type="text" placeholder="🔍 Search WO#, address, unit…"
                value={search} onChange={(e) => setSearch(e.target.value)}
                data-testid="v2-pool-search"
                className="w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:border-[#00C4CC]"/>
            </div>
            <div className="flex gap-1.5 mb-2.5">
              {[["all", "All"], ["emergency", "🚨 Emergency"], ["turns", "Turns"]].map(([key, label]) => (
                <button key={key} onClick={() => setFilter(key)} data-testid={`v2-filter-${key}`}
                  className={`h-7 px-2.5 rounded-full text-[11px] font-bold border transition-colors
                    ${filter === key
                      ? (key === "emergency"
                          ? "bg-red-50 border-red-500 text-red-600"
                          : "bg-[#0B1F3A] border-[#0B1F3A] text-white")
                      : "bg-white border-slate-200 text-[#0B1F3A] hover:border-slate-400"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-1.5 overflow-y-auto"
              style={{ maxHeight: isMobile ? "calc(100vh - 340px)" : "calc(100vh - 260px)" }}>
              {/* Pinned matches — surfaced above pool so dispatcher sees the
                  WO is already on a truck instead of thinking it doesn't exist. */}
              {search.trim() && pinnedHits.length > 0 && (
                <div className="mb-1 rounded-lg border border-amber-300 bg-amber-50 p-2" data-testid="v2-pinned-hits">
                  <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 mb-1">
                    Already pinned · {pinnedHits.length}
                  </div>
                  <div className="flex flex-col gap-1">
                    {pinnedHits.slice(0, 5).map(p => (
                      <Link key={p.id} to={`/admin/jobs/${p.id}`} data-testid={`v2-pinned-hit-${p.id}`}
                        className="block text-[11px] text-amber-900 hover:text-amber-700 leading-tight">
                        <b>WO {p.work_order_number || p.id.slice(0, 6)}</b>
                        <span className="ml-1">on <b>{truckLabelFor(p.truck_key)}</b></span>
                        <div className="text-[10px] text-amber-700 truncate">{p.property_address}{p.unit ? ` · ${formatUnit(p.unit)}` : ""}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {filteredPool.length === 0 && !search.trim() && (
                <div className="text-xs text-slate-400 text-center p-4">Pool is empty. 🎉</div>
              )}
              {filteredPool.length === 0 && search.trim() && pinnedHits.length === 0 && (
                <div className="text-center p-4">
                  <div className="text-xs text-slate-500 font-bold">No open WOs match "{search}".</div>
                  <Link to={`/admin/jobs?q=${encodeURIComponent(search)}`}
                    data-testid="v2-search-all-jobs"
                    className="mt-2 inline-block text-[11px] font-bold text-[#00C4CC] hover:underline">
                    🔎 Search all jobs (incl. completed) →
                  </Link>
                </div>
              )}
              {filteredPool.map(j => (
                <PoolItem key={j.id} job={j} onPin={() => setPinTarget({ job: j })}
                  selected={selectedPool.has(j.id)}
                  onToggleSelect={() => togglePoolSelect(j.id)}
                  testid={`v2-pool-${j.id}`}/>
              ))}
            </div>
          </div>
        </div>

        {/* Publish bar */}
        <div className="sticky bottom-0 z-30 bg-white border-t border-slate-200 px-5 py-3.5 flex items-center justify-between">
          <div className="text-[13px] text-slate-600" data-testid="v2-summary">
            <b>{totalPinned}</b> WOs pinned across <b>{totalTrucks}</b> trucks · <b>{pool.length}</b> remaining in pool
          </div>
          <div className="flex gap-2">
            <Link to="/admin/jobs-board" data-testid="v2-save-draft"
              className="h-11 px-5 inline-flex items-center rounded-lg bg-white border border-slate-200 text-[13px] font-semibold text-[#0B1F3A]">
              View jobs board
            </Link>
            <button onClick={publish} disabled={publishing || totalPinned === 0}
              data-testid="v2-publish"
              className="h-11 px-5 rounded-lg bg-[#00C4CC] hover:bg-[#00a8af] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] font-bold shadow-[0_4px_14px_rgba(0,196,204,0.35)]">
              {publishing ? "Publishing…" : `Publish · notify crew`}
            </button>
          </div>
        </div>

        {/* Pin-target modal */}
        {pinTarget && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={() => setPinTarget(null)} data-testid="v2-pin-modal-backdrop">
            <div className="bg-white rounded-2xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="v2-pin-modal">
              <div className="text-lg font-bold text-[#0B1F3A] mb-1">Pin to which truck?</div>
              <div className="text-xs text-slate-500 mb-4">
                WO {pinTarget.job.work_order_number} · {pinTarget.job.property_address}
              </div>
              {trucks.length === 0 && (
                <div className="text-sm text-slate-500 p-3 bg-slate-50 rounded-lg">
                  Turn a tech ON in the left column first.
                </div>
              )}
              <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
                {trucks.map(t => (
                  <button key={t.truck_key} onClick={() => pinToTruck(pinTarget.job, t)}
                    data-testid={`v2-pin-to-${t.truck_key}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-white hover:bg-[#E6FAFB] border border-slate-200 hover:border-[#00C4CC] text-left transition-colors">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00C4CC] to-[#0B1F3A] text-white font-bold text-xs flex items-center justify-center flex-shrink-0">
                      {t.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#0B1F3A] truncate">{t.label}</div>
                      <div className="text-[11px] text-slate-500">{t.jobs.length} stop{t.jobs.length === 1 ? "" : "s"}</div>
                    </div>
                    <span className="text-[#00C4CC] font-bold">Pin →</span>
                  </button>
                ))}
              </div>
              <button onClick={() => setPinTarget(null)} data-testid="v2-pin-modal-cancel"
                className="mt-4 w-full h-10 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#0B1F3A] text-sm font-bold">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Divide Pool preview modal */}
        {splitPreview && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={() => setSplitPreview(null)} data-testid="v2-divide-modal-backdrop">
            <div className="bg-white rounded-2xl p-5 w-full max-w-2xl max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()} data-testid="v2-divide-modal">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-lg font-bold text-[#0B1F3A]">Divide Pool preview</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {splitPreview.assignments.reduce((n, a) => n + a.job_count, 0)} WOs ·
                    {" "}{splitPreview.assignments.length} trucks ·
                    {" "}{date}
                    {splitPreview.unlocatable_count > 0 ? ` · ${splitPreview.unlocatable_count} without coords (round-robin)` : ""}
                  </div>
                </div>
                <button onClick={() => setSplitPreview(null)} data-testid="v2-divide-close"
                  className="text-slate-400 hover:text-slate-900 text-xl leading-none">×</button>
              </div>
              <div className="flex-1 overflow-y-auto -mx-2 px-2">
                <div className="space-y-3">
                  {splitPreview.assignments.map(a => (
                    <div key={a.truck_key} data-testid={`v2-divide-truck-${a.truck_key}`}
                      className="border border-slate-200 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="text-sm font-bold text-[#0B1F3A]">{a.truck_label}</div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                          {a.job_count} stop{a.job_count === 1 ? "" : "s"} · {a.load_miles} mi
                        </div>
                      </div>
                      {a.jobs.length === 0 ? (
                        <div className="text-xs text-slate-400 italic">— no stops assigned —</div>
                      ) : (
                        <div className="space-y-1">
                          {a.jobs.map(j => (
                            <div key={j.job_id} className="flex items-center justify-between text-xs text-slate-700">
                              <div className="min-w-0 flex-1 truncate">
                                <span className="font-mono text-slate-500">WO {j.wo || j.job_id.slice(0, 6)}</span>
                                <span className="mx-1 text-slate-400">·</span>
                                {j.address}{j.unit ? ` · ${j.unit}` : ""}
                              </div>
                              {j.miles_from_cursor != null && (
                                <span className="ml-2 text-[10px] font-bold text-slate-400 flex-shrink-0">
                                  {j.miles_from_cursor} mi
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-3 border-t border-slate-200">
                <button onClick={() => setSplitPreview(null)} data-testid="v2-divide-cancel"
                  className="flex-1 h-11 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#0B1F3A] font-bold text-sm">
                  Cancel
                </button>
                <button onClick={() => runAutoSplit(false)} disabled={splitBusy}
                  data-testid="v2-divide-commit"
                  className="flex-1 h-11 rounded-lg bg-[#00C4CC] hover:bg-[#00a8af] disabled:opacity-40 text-white font-black text-sm uppercase tracking-widest">
                  {splitBusy ? "Pinning…" : `Pin all ${splitPreview.assignments.reduce((n, a) => n + a.job_count, 0)}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────
function WeatherChip({ date, weather }) {
  const day = (weather || []).find(d => d.date === date);
  if (!day) return null;

  // Choose the alarm color: rainy/windy/cold days deserve dispatcher's eye
  const isWet = day.is_wet;
  const isCold = day.hi < 40;
  const isWindy = day.wind_max > 20;
  const isHot = day.hi > 90;
  const tint = isWet   ? "bg-blue-50 text-blue-800 border-blue-300"
             : isCold  ? "bg-slate-100 text-slate-700 border-slate-300"
             : isWindy ? "bg-amber-50 text-amber-800 border-amber-300"
             : isHot   ? "bg-red-50 text-red-800 border-red-300"
             :           "bg-emerald-50 text-emerald-800 border-emerald-300";

  const title = `${day.label} · Hi ${day.hi}° / Lo ${day.lo}°${day.precip_prob ? ` · ${day.precip_prob}% rain` : ""}${day.wind_max ? ` · wind ${day.wind_max}` : ""}`;

  return (
    <span title={title} data-testid="v2-weather-chip"
      className={`h-9 inline-flex items-center gap-1.5 px-2.5 rounded-lg border text-[11px] font-bold ${tint}`}>
      <span className="text-base leading-none">{day.icon || "☀️"}</span>
      <span className="font-black">{day.hi}°</span>
      {day.precip_prob >= 30 && (
        <span className="opacity-80">💧{day.precip_prob}%</span>
      )}
      {isWindy && !isWet && <span className="opacity-80">💨{day.wind_max}</span>}
    </span>
  );
}


function TechChip({ worker, on, off, jobCount, onClick }) {
  const initials = (worker.name || worker.email || "??").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <button onClick={onClick} data-testid={`v2-tech-${worker.id}`}
      className={`bg-white rounded-xl px-3 py-2.5 flex items-center gap-3 border-2 transition-all text-left
        ${on ? "border-transparent shadow-sm hover:border-[#00C4CC] hover:-translate-y-0.5"
          : "border-transparent opacity-50 hover:opacity-100"}`}>
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00C4CC] to-[#0B1F3A] text-white font-bold text-sm flex items-center justify-center flex-shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#0B1F3A] truncate">{worker.name || worker.email}</div>
        <div className="text-[11px] text-slate-500">
          {on ? (jobCount > 0 ? `${jobCount} stop${jobCount === 1 ? "" : "s"} pinned` : "Ready · no stops yet") : "Off tomorrow"}
        </div>
      </div>
      {on && jobCount > 0 && (
        <span className="bg-[#00C4CC] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{jobCount}</span>
      )}
    </button>
  );
}

function TruckCard({ truck, busy, onAutoCluster, onUnpin, onMarkDone, onReorder, date, onPromoted }) {
  const [target, setTarget] = useState(Math.max(8, truck.jobs.length));
  const [openMenu, setOpenMenu] = useState(null); // jobId
  const [nextUp, setNextUp] = useState(null);
  const [nextUpBusy, setNextUpBusy] = useState(false);
  // Job IDs the dispatcher has skipped this session — passed to the API so
  // Next Up walks deeper into the ranked list instead of re-serving the same
  // top-5 every time.
  const [dismissedIds, setDismissedIds] = useState(() => new Set());

  const loadNextUp = useCallback(async (opts = {}) => {
    setNextUpBusy(true);
    try {
      const excludeSet = opts.reset ? new Set() : dismissedIds;
      if (opts.reset) setDismissedIds(new Set());
      const exclude = Array.from(excludeSet).join(",");
      const q = `truck_key=${encodeURIComponent(truck.truck_key)}&date=${date}&limit=5${exclude ? `&exclude=${exclude}` : ""}`;
      const r = await api.get(`/jobs/next-up?${q}`);
      setNextUp(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Next Up failed"));
    } finally { setNextUpBusy(false); }
  }, [truck.truck_key, date, dismissedIds]);

  // Skip one candidate — add to dismissed set + re-fetch so the next
  // highest-ranked WO slides into its place.
  const skipCandidate = async (jobId) => {
    setDismissedIds(prev => {
      const next = new Set(prev);
      next.add(jobId);
      return next;
    });
    // Re-fetch with the new exclude list (dismissedIds is stale in this
    // closure, so we pass an override).
    setNextUpBusy(true);
    try {
      const nextSet = new Set(dismissedIds);
      nextSet.add(jobId);
      const exclude = Array.from(nextSet).join(",");
      const r = await api.get(`/jobs/next-up?truck_key=${encodeURIComponent(truck.truck_key)}&date=${date}&limit=5&exclude=${exclude}`);
      setNextUp(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Skip failed"));
    } finally { setNextUpBusy(false); }
  };

  // Auto-load when route is running low (≤2 stops remaining)
  useEffect(() => {
    if (truck.jobs.length > 0 && truck.jobs.length <= 2 && nextUp === null) {
      loadNextUp();
    }
  }, [truck.jobs.length, nextUp, loadNextUp]);

  const promote = async (jobId) => {
    try {
      await api.post("/jobs/promote-to-truck", {
        truck_key: truck.truck_key, truck_label: truck.label, date, job_id: jobId,
      });
      toast.success(`Promoted to ${truck.label}`);
      await loadNextUp();
      onPromoted && onPromoted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Promote failed"));
    }
  };

  const sendMoreWork = async () => {
    try {
      const r = await api.post("/jobs/send-more-work", {
        truck_key: truck.truck_key, truck_label: truck.label, date, note: "Dispatcher-initiated",
      });
      if (r.data.silenced) {
        toast("Comms silence is on — nothing was sent to #office");
      } else {
        toast.success(`Pinged #office · ${r.data.candidates.length} candidates suggested`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Alert failed"));
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid={`v2-truck-${truck.truck_key}`}>
      <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-100">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00C4CC] to-[#0B1F3A] text-white font-bold text-xs flex items-center justify-center flex-shrink-0">
          {truck.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#0B1F3A] truncate">{truck.label}</div>
          <div className="text-[11px] text-slate-500">{truck.jobs.length} stop{truck.jobs.length === 1 ? "" : "s"}</div>
        </div>
        {/* Clear-route escape hatch — one tap unpins EVERY WO on this
            truck. Fastest way to reset when a route gets messed up. */}
        {truck.jobs.length > 0 && (
          <button
            onClick={async () => {
              if (!window.confirm(
                `Clear ${truck.jobs.length} stop${truck.jobs.length === 1 ? "" : "s"} from ${truck.label}'s route?\n\n` +
                `Each WO goes back to the unpinned pool — nothing gets deleted or completed.`
              )) return;
              try {
                await api.post("/jobs/sweeper-unpin", {
                  job_ids: truck.jobs.map(j => j.id),
                });
                toast.success(`${truck.label}: cleared ${truck.jobs.length} stop${truck.jobs.length === 1 ? "" : "s"}`);
                onPromoted?.();   // reuse the parent's reload hook
              } catch (e) {
                toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't clear route"));
              }
            }}
            data-testid={`v2-clear-route-${truck.truck_key}`}
            title="Clear this route — sends every WO back to the pool"
            className="text-[10px] font-black uppercase tracking-widest text-red-600 hover:text-white hover:bg-red-600 border-2 border-red-600 rounded-md px-2 py-1 transition-colors">
            Clear
          </button>
        )}
      </div>
      <div className="px-4 py-3 flex flex-col gap-2">
        {truck.jobs.length === 0 && (
          <div className="text-xs text-slate-400 p-4 text-center bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
            Tap a WO in the right column → pick this truck
          </div>
        )}
        {truck.jobs.map((j, idx) => {
          const fillBadge = (j.sweeper_pin || {}).pinned_by_fill;
          const isOpen = openMenu === j.id;
          return (
            <div key={j.id} className="relative">
              <div className={`flex items-start gap-2.5 p-2.5 rounded-lg
                ${fillBadge ? "bg-[#E6FAFB] hover:bg-[#d0f5f7]" : "bg-slate-50 hover:bg-slate-100"}
                ${isOpen ? "ring-2 ring-[#00C4CC]" : ""}`}
                data-testid={`v2-pin-${j.id}`}>
                <div className="w-6 h-6 rounded-full bg-[#00C4CC] text-white font-bold text-xs flex items-center justify-center flex-shrink-0">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold font-mono text-slate-500 flex items-center gap-1.5">
                    WO {j.work_order_number || j.id.slice(0, 6)}
                    {fillBadge && <span className="bg-[#00C4CC] text-white text-[9px] px-1.5 py-0.5 rounded normal-case">FILL</span>}
                  </div>
                  <div className="text-[13px] font-semibold text-[#0B1F3A] truncate">
                    {formatAddressUnit(j.property_address, j.unit)}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{j.title}</div>
                  <div className="flex gap-1.5 mt-1">
                    {j.priority && (j.priority.toLowerCase() === "urgent" || j.priority.toLowerCase() === "emergency") && (
                      <span className="text-[9px] font-bold uppercase bg-red-100 text-red-700 px-1.5 py-0.5 rounded">🚨 Emergency</span>
                    )}
                    {j.is_turn && <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Turn</span>}
                    {j.return_visit?.date && <span className="text-[9px] font-bold uppercase bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">Return</span>}
                  </div>
                </div>
                <button onClick={() => setOpenMenu(isOpen ? null : j.id)}
                  data-testid={`v2-pin-menu-${j.id}`}
                  className="w-8 h-8 rounded-full hover:bg-white/80 text-slate-400 hover:text-[#0B1F3A] font-bold text-lg flex items-center justify-center flex-shrink-0">
                  ⋯
                </button>
                {onReorder && truck.jobs.length > 1 && (
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button
                      disabled={idx === 0}
                      onClick={() => {
                        const ids = truck.jobs.map(x => x.id);
                        [ids[idx - 1], ids[idx]] = [ids[idx], ids[idx - 1]];
                        onReorder(ids);
                      }}
                      data-testid={`v2-pin-up-${j.id}`}
                      title="Move up"
                      className="w-6 h-5 rounded bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-[11px] font-bold text-slate-600 flex items-center justify-center">
                      ▲
                    </button>
                    <button
                      disabled={idx === truck.jobs.length - 1}
                      onClick={() => {
                        const ids = truck.jobs.map(x => x.id);
                        [ids[idx], ids[idx + 1]] = [ids[idx + 1], ids[idx]];
                        onReorder(ids);
                      }}
                      data-testid={`v2-pin-down-${j.id}`}
                      title="Move down"
                      className="w-6 h-5 rounded bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-[11px] font-bold text-slate-600 flex items-center justify-center">
                      ▼
                    </button>
                  </div>
                )}
              </div>

              {isOpen && (
                <div className="absolute right-1 top-11 z-20 bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[180px] overflow-hidden"
                  data-testid={`v2-pin-actions-${j.id}`}>
                  <button onClick={() => { setOpenMenu(null); onMarkDone(j); }}
                    data-testid={`v2-pin-done-${j.id}`}
                    className="w-full px-3 py-2.5 text-left text-sm text-emerald-700 hover:bg-emerald-50 flex items-center gap-2 font-semibold">
                    <span>✅</span> Mark done
                  </button>
                  <button onClick={() => { setOpenMenu(null); onUnpin(j.id); }}
                    data-testid={`v2-unpin-${j.id}`}
                    className="w-full px-3 py-2.5 text-left text-sm text-[#0B1F3A] hover:bg-slate-50 flex items-center gap-2 font-semibold">
                    <span>↩</span> Back to pool
                  </button>
                  <a href={`/admin/jobs/${j.id}`} target="_blank" rel="noreferrer"
                    data-testid={`v2-pin-open-${j.id}`}
                    onClick={() => setOpenMenu(null)}
                    className="w-full px-3 py-2.5 text-left text-sm text-[#0B1F3A] hover:bg-slate-50 flex items-center gap-2 font-semibold">
                    <span>↗</span> Open job
                  </a>
                </div>
              )}
            </div>
          );
        })}

        {/* Next Up bench — surfaces when route runs low OR on-demand */}
        {(truck.jobs.length > 0) && (
          <div className="mt-1 border-t border-dashed border-slate-200 pt-2">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                Next Up · {nextUp?.candidates?.length || 0}
                {dismissedIds.size > 0 && (
                  <span className="ml-1 text-slate-400 normal-case font-bold">
                    ({dismissedIds.size} skipped)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => loadNextUp()}
                  disabled={nextUpBusy}
                  data-testid={`v2-nextup-refresh-${truck.truck_key}`}
                  className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC] hover:text-[#008a8f] disabled:opacity-40"
                >{nextUp ? "↻ refresh" : (nextUpBusy ? "Loading…" : "▸ Suggest")}</button>
                {dismissedIds.size > 0 && (
                  <button
                    onClick={() => loadNextUp({ reset: true })}
                    disabled={nextUpBusy}
                    data-testid={`v2-nextup-reset-${truck.truck_key}`}
                    className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-800 ml-1"
                    title="Clear skipped picks and start over"
                  >⟲ reset</button>
                )}
                <button
                  onClick={sendMoreWork}
                  data-testid={`v2-send-more-work-${truck.truck_key}`}
                  className="text-[10px] font-black uppercase tracking-widest text-rose-600 hover:text-rose-800 ml-2"
                  title="Ping #office TeamChat to start prepping the next entries"
                >📣 Alert Office</button>
              </div>
            </div>
            {nextUp && nextUp.candidates?.length === 0 && (
              <div className="text-[11px] text-slate-400 italic p-2 text-center bg-slate-50 rounded">
                {nextUp.warning === "no_geocoded_route"
                  ? "Route has no coords yet — Fill & Sort first"
                  : "No nearby unpinned WOs — call dispatcher"}
              </div>
            )}
            {nextUp?.candidates?.map(c => (
              <div key={c.id} className="flex items-center gap-2 p-2 mt-1 bg-cyan-50 hover:bg-cyan-100 rounded-lg"
                data-testid={`v2-nextup-${c.id}`}>
                <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 font-bold text-xs flex items-center justify-center flex-shrink-0" title={`${(c.score * 100).toFixed(0)}% match`}>
                  {c.priority === "emergency" || c.priority === "urgent" ? "🚨" : "+"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold font-mono text-slate-600">
                    WO {c.wo || c.id.slice(0, 6)} · <span className="text-cyan-700">{c.distance_mi} mi</span>
                    {c.why?.priority && <span className="ml-1 text-red-600">urgent</span>}
                    {c.why?.portfolio_match && <span className="ml-1 text-violet-600">same portfolio</span>}
                    {c.why?.vendor_match && <span className="ml-1 text-amber-600">same vendor</span>}
                  </div>
                  <div className="text-[12px] font-semibold text-[#0B1F3A] truncate">
                    {c.address}{c.unit ? ` #${c.unit}` : ""}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{c.title}</div>
                </div>
                <button
                  onClick={() => skipCandidate(c.id)}
                  disabled={nextUpBusy}
                  data-testid={`v2-nextup-skip-${c.id}`}
                  title="Not this one — show the next pick"
                  className="shrink-0 h-8 px-2.5 bg-white hover:bg-slate-100 border border-slate-300 text-slate-600 text-[10px] font-black uppercase tracking-widest rounded disabled:opacity-40"
                >Skip</button>
                <button
                  onClick={() => promote(c.id)}
                  data-testid={`v2-nextup-promote-${c.id}`}
                  className="shrink-0 h-8 px-3 bg-[#00C4CC] hover:bg-[#008a8f] text-white text-[10px] font-black uppercase tracking-widest rounded"
                >Promote</button>
              </div>
            ))}
          </div>
        )}
      </div>
      {truck.jobs.length > 0 && (
        <div className="px-4 py-2.5 flex items-center justify-between bg-slate-50 border-t border-slate-100 text-xs gap-2 flex-wrap">
          <span className="text-slate-500 font-medium">{truck.jobs.length} stop{truck.jobs.length === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2">
            <label className="text-slate-500 font-medium flex items-center gap-1">
              Fill to
              <input type="number" min="1" max="50" value={target}
                onChange={(e) => setTarget(Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1)))}
                data-testid={`v2-fill-target-${truck.truck_key}`}
                className="w-12 h-7 px-1.5 rounded border border-slate-200 text-center text-[#0B1F3A] font-bold text-xs"/>
              stops
            </label>
            <button onClick={() => onAutoCluster(target)} disabled={busy}
              data-testid={`v2-auto-cluster-${truck.truck_key}`}
              className="h-7 px-2.5 rounded bg-[#00C4CC] hover:bg-[#00a8af] text-white font-bold disabled:opacity-50">
              {busy ? "Filling…" : (target > truck.jobs.length ? "Fill & sort ⚡" : "Sort ⚡")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PoolItem({ job, onPin, selected, onToggleSelect, testid }) {
  const pri = (job.priority || "").toLowerCase();
  const isEmergency = pri === "urgent" || pri === "emergency";
  const isTurn = job.is_turn;
  const pill = isEmergency
    ? { text: "EM", cls: "bg-red-100 text-red-700" }
    : isTurn
      ? { text: "TR", cls: "bg-amber-100 text-amber-800" }
      : { text: "RT", cls: "bg-slate-100 text-slate-600" };
  return (
    <div data-testid={testid}
      className={`bg-white rounded-lg p-2.5 border shadow-sm text-left transition-all flex items-start gap-2
        ${selected ? "border-[#00C4CC] bg-cyan-50 shadow-[0_0_0_2px_rgba(0,196,204,0.25)]" : "border-transparent hover:border-[#00C4CC]"}`}>
      {onToggleSelect && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          data-testid={`${testid}-select`}
          aria-label={selected ? "Unselect WO" : "Select WO for Divide Pool"}
          className={`mt-0.5 h-5 w-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors
            ${selected ? "bg-[#00C4CC] border-[#00C4CC] text-white" : "bg-white border-slate-300 hover:border-[#00C4CC]"}`}>
          {selected ? <span className="text-[12px] font-black leading-none">✓</span> : null}
        </button>
      )}
      <button onClick={onPin} data-testid={`${testid}-pin`} className="min-w-0 flex-1 text-left">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold text-slate-500 font-mono">WO {job.work_order_number || job.id?.slice(0, 6)}</div>
            <div className="text-[13px] font-semibold text-[#0B1F3A] truncate">
              {formatAddressUnit(job.property_address, job.unit)}
            </div>
          </div>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${pill.cls}`}>{pill.text}</span>
        </div>
        <div className="text-[11px] text-slate-500 truncate mt-0.5">{job.title || job.description || ""}</div>
      </button>
    </div>
  );
}
